// Paginated wrapper for set-returning Supabase RPCs.
//
// PostgREST caps every response at max-rows (1000 on our project), and it does
// so SILENTLY — `supabase.rpc()` returns the first 1000 rows with no error.
// get_launch_lineup_data now emits >1500 rows (one tile per CC × status × date
// since v1.19), so a single call truncates mid-dataset: sorted ascending by
// launch date, everything past row 1000 (~Oct 1 as of Aug 2026) just vanished
// from Launch Line Up and the Round Up Report.
//
// This fetches in PAGE_SIZE windows via .range() and concatenates. We only
// request another page when the previous one came back at exactly PAGE_SIZE —
// a longer page means the client ignored the range (renderMode.js shim serving
// the injected bundle), so looping would duplicate rows forever.
//
// PAGE_SIZE must equal the server's max-rows: if it were larger, real
// PostgREST pages would come back "short" at 1000 and we'd stop early.
const PAGE_SIZE = 1000;
const MAX_PAGES = 30; // runaway guard: 30k rows is far beyond any real dataset

// orderCol: offset paging over an unordered set can dup/drop rows between
// pages — pass the RPC's unique key column to make page boundaries stable.
export async function rpcAll(client, fn, args, orderCol) {
  const all = [];
  let prevFirst = null;
  for (let page = 0; page < MAX_PAGES; page++) {
    const from = page * PAGE_SIZE;
    let q = client.rpc(fn, args).range(from, from + PAGE_SIZE - 1);
    if (orderCol) q = q.order(orderCol, { ascending: true });
    const { data, error } = await q;
    if (error) return { data: null, error };
    // A page that opens with the same row as the previous page means the
    // client ignored .range() and replayed the whole set — stop before
    // concatenating a duplicate (2026-08-13: an exactly-1000-row export
    // bundle defeated the short-page rule below and rendered 30× tiles).
    const first = data && data.length ? JSON.stringify(data[0]) : null;
    if (page > 0 && first !== null && first === prevFirst) break;
    prevFirst = first;
    all.push(...(data || []));
    if (!data || data.length !== PAGE_SIZE) break;
  }
  return { data: all, error: null };
}
